// read-text-files.ts — read attached text/markdown files into a prompt block.
// Text-only on purpose (this phase): .md/.txt/.csv/.json/.markdown/.log/.yml/.yaml.
// Binary formats (pdf/docx/images) are rejected with a clear reason so the UI can
// warn instead of feeding garbage bytes to the model.

export interface AttachedFile {
  name: string;
  text: string;
}

/** Per-file and total size caps (chars) — keeps the prompt within model limits. */
const PER_FILE_MAX = 40_000;
const TOTAL_MAX = 120_000;

const TEXT_EXT = /\.(md|markdown|txt|text|csv|tsv|json|log|ya?ml)$/i;

/** True when a File looks like readable text (by MIME or extension). */
export function isTextFile(file: File): boolean {
  if (file.type.startsWith("text/")) return true;
  if (file.type === "application/json") return true;
  return TEXT_EXT.test(file.name);
}

/**
 * Read text files into { name, text }. Non-text files are skipped and reported
 * in `rejected`. Content is truncated per-file and overall to stay within limits.
 */
export async function readTextFiles(
  files: File[],
): Promise<{ accepted: AttachedFile[]; rejected: string[] }> {
  const accepted: AttachedFile[] = [];
  const rejected: string[] = [];
  let total = 0;

  for (const file of files) {
    if (!isTextFile(file)) {
      rejected.push(file.name);
      continue;
    }
    let text = await file.text();
    if (text.length > PER_FILE_MAX) text = `${text.slice(0, PER_FILE_MAX)}\n…(đã cắt bớt)`;
    if (total + text.length > TOTAL_MAX) {
      text = text.slice(0, Math.max(0, TOTAL_MAX - total));
      if (text) text += "\n…(đã cắt bớt)";
    }
    if (!text) continue;
    accepted.push({ name: file.name, text });
    total += text.length;
    if (total >= TOTAL_MAX) break;
  }

  return { accepted, rejected };
}

/**
 * Format attached files as a fenced prompt block prepended to the user message.
 * Returns "" when there are no files.
 */
export function formatFilesForPrompt(files: AttachedFile[]): string {
  if (files.length === 0) return "";
  const blocks = files
    .map((f) => `### Tệp đính kèm: ${f.name}\n\`\`\`\n${f.text}\n\`\`\``)
    .join("\n\n");
  return `Người dùng đã đính kèm ${files.length} tệp. Hãy đọc kỹ và thực hiện đúng yêu cầu/hướng dẫn trong tệp.\n\n${blocks}`;
}
